import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios, { AxiosResponse } from 'axios';
import { Commission, CommissionsSearch, CommissionWithRelations } from 'src/models/commission.model';
import { Pagination } from 'src/models/pagination.model';
import { closeModal } from './modal.reducer';

type InitialState = {
  loading: boolean;
  failure: boolean;
  success: boolean;
  commissions: CommissionsSearch[];
  totalItems: number;
  refresh: number;
  loadingSearch: boolean;
  failureSearch: boolean;
  successSearch: boolean;
  commissionDetail: CommissionWithRelations;
};

const INITIAL_STATE: InitialState = {
  loading: false,
  failure: false,
  success: false,
  commissions: [] as CommissionsSearch[],
  totalItems: 0,
  refresh: 0,
  loadingSearch: false,
  failureSearch: false,
  successSearch: false,
  commissionDetail: null as unknown as CommissionWithRelations,
};

export const getCommissionsSearch = createAsyncThunk(
  'commissions/search/get',
  async (data: { pagination: Pagination }, { rejectWithValue }) => {
    try {
      const response: AxiosResponse<{
        data: CommissionsSearch[];
        totalItems: number;
      }> = await axios.get(`/api/commissions/search`, {
        params: data.pagination,
      });

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const getCommission = createAsyncThunk(
  'commission-relations/post',
  async (
    data: {
      commissionId: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const response: AxiosResponse<CommissionWithRelations> = await axios.get(`/api/commission/${data.commissionId}`);

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const createCommission = createAsyncThunk('commission/post', async (commission: Commission, { rejectWithValue, dispatch }) => {
  try {
    const response: AxiosResponse<Commission> = await axios.post(`/api/commission`, commission);
    dispatch(refreshCommissions());
    dispatch(closeModal());

    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});

export const deleteCommission = createAsyncThunk('roles/delete', async (data: { roleId: number }, { rejectWithValue, dispatch }) => {
  try {
    await axios.delete(`/api/commission/${data.roleId}`);
    dispatch(refreshCommissions());
    dispatch(closeModal());
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});

export const updateCommissionName = createAsyncThunk(
  'roles/update',
  async (data: { commissionId: number; newName: string }, { rejectWithValue, dispatch }) => {
    try {
      await axios.put(`/api/commission/name`, { commissionId: data.commissionId, name: data.newName });
      dispatch(refreshCommissions());
      dispatch(closeModal());
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const commissionWithRelationsSlice = createSlice({
  name: 'commissions-relations',
  initialState: INITIAL_STATE,
  reducers: {
    refreshCommissions: (state: InitialState) => {
      return {
        ...state,
        refresh: state.refresh + 1,
      };
    },
    refreshCommissionRelations: (state: InitialState) => {
      return {
        ...state,
        refresh: state.refresh + 1,
      };
    },
  },
  extraReducers: builder => {
    builder.addCase(getCommissionsSearch.pending, (state: InitialState) => {
      return {
        ...state,
        loadingSearch: true,
        failureSearch: false,
        successSearch: false,
      };
    });
    builder.addCase(
      getCommissionsSearch.fulfilled,
      (state: InitialState, action: PayloadAction<{ data: CommissionsSearch[]; totalItems: number }>) => {
        return {
          ...state,
          loadingSearch: false,
          failureSearch: false,
          successSearch: true,
          commissions: action.payload.data,
          totalItems: action.payload.totalItems,
        };
      }
    );
    builder.addCase(getCommissionsSearch.rejected, (state: InitialState) => {
      return {
        ...state,
        loadingSearch: false,
        failureSearch: true,
        successSearch: false,
      };
    });
    builder.addCase(getCommission.pending, (state: InitialState) => {
      return {
        ...state,
        loading: true,
        failure: false,
        success: false,
      };
    });
    builder.addCase(getCommission.fulfilled, (state: InitialState, action: PayloadAction<CommissionWithRelations>) => {
      return {
        ...state,
        loading: false,
        failure: false,
        success: true,
        commissionDetail: action.payload,
      };
    });
    builder.addCase(getCommission.rejected, (state: InitialState) => {
      return {
        ...state,
        loading: false,
        failure: true,
        success: false,
      };
    });
    builder.addCase(createCommission.pending, (state: InitialState) => {
      return {
        ...state,
        loading: true,
        failure: false,
        success: false,
      };
    });

    builder.addCase(createCommission.fulfilled, (state: InitialState, action: PayloadAction<Commission>) => {
      return {
        ...state,
        loading: false,
        failure: false,
        success: true,
        newCommission: action.payload,
      };
    });

    builder.addCase(createCommission.rejected, (state: InitialState) => {
      return {
        ...state,
        loading: false,
        failure: true,
        success: false,
      };
    });
    builder.addCase(deleteCommission.pending, (state: InitialState) => {
      return {
        ...state,
        loading: true,
        failure: false,
        success: false,
      };
    });
    builder.addCase(deleteCommission.fulfilled, (state: InitialState) => {
      return {
        ...state,
        loading: false,
        failure: false,
        success: true,
      };
    });
    builder.addCase(deleteCommission.rejected, (state: InitialState) => {
      return {
        ...state,
        loading: false,
        failure: true,
        success: false,
      };
    });
    builder.addCase(updateCommissionName.pending, (state: InitialState) => {
      return {
        ...state,
        loading: true,
        failure: false,
        success: false,
      };
    });
    builder.addCase(updateCommissionName.fulfilled, (state: InitialState) => {
      return {
        ...state,
        loading: false,
        failure: false,
        success: true,
      };
    });
    builder.addCase(updateCommissionName.rejected, (state: InitialState) => {
      return {
        ...state,
        loading: false,
        failure: true,
        success: false,
      };
    });
  },
});

export const { refreshCommissions, refreshCommissionRelations } = commissionWithRelationsSlice.actions;

export default commissionWithRelationsSlice.reducer;
